/**
 * Arteriel Favorites — app-proxy API client + UI behaviors.
 *
 * API: /apps/arteriel-ai/favorites (favorites Lambda, see platform repo
 * services/ai-service/lambdas/favorites/README.md for the contract).
 *
 * Used by:
 *   - snippets/favorite-button.liquid  (PDP heart, [data-favorite-button])
 *   - sections/main-favorites.liquid   (favorites page)
 *
 * State shape:
 *   {version, saved: [{id, handle, saved_at}], collections: [{id, name, items}]}
 */

const API_BASE = '/apps/arteriel-ai/favorites';
const CACHE_KEY = 'arteriel:favorites';

/* ---------------- API client ---------------- */

async function api(path = '', body = null) {
  const opts = body
    ? { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) }
    : { method: 'GET' };
  const res = await fetch(`${API_BASE}${path}`, opts);
  if (res.status === 401) throw Object.assign(new Error('sign_in_required'), { signIn: true });
  if (!res.ok) throw new Error(`favorites api ${res.status}`);
  const state = await res.json();
  try { sessionStorage.setItem(CACHE_KEY, JSON.stringify(state)); } catch { /* private mode */ }
  return state;
}

const FavoritesAPI = {
  get: () => api(),
  save: (productId, handle) => api('/save', { product_id: productId, handle }),
  unsave: (productId) => api('/unsave', { product_id: productId }),
  collectionCreate: (name) => api('/collection-create', { name }),
  collectionRename: (id, name) => api('/collection-rename', { id, name }),
  collectionDelete: (id) => api('/collection-delete', { id }),
  collectionAdd: (id, productId) => api('/collection-add', { id, product_id: productId }),
  collectionRemove: (id, productId) => api('/collection-remove', { id, product_id: productId }),
  cached() {
    try { return JSON.parse(sessionStorage.getItem(CACHE_KEY)); } catch { return null; }
  },
};

/* ---------------- Toast ---------------- */

function toast(message) {
  let el = document.querySelector('.favorites-toast');
  if (!el) {
    el = document.createElement('div');
    el.className = 'favorites-toast';
    document.body.appendChild(el);
  }
  el.textContent = message;
  el.classList.add('is-visible');
  clearTimeout(el._hideTimer);
  el._hideTimer = setTimeout(() => el.classList.remove('is-visible'), 2400);
}

/* ---------------- PDP heart ---------------- */

function initHearts() {
  const buttons = document.querySelectorAll('[data-favorite-button]');
  if (!buttons.length) return;

  // Reflect saved state from cache instantly, then refresh from the API.
  const reflect = (state) => {
    if (!state) return;
    const ids = new Set(state.saved.map((s) => s.id));
    buttons.forEach((btn) => {
      btn.setAttribute('aria-pressed', ids.has(Number(btn.dataset.productId)) ? 'true' : 'false');
    });
  };
  reflect(FavoritesAPI.cached());
  FavoritesAPI.get().then(reflect).catch(() => {});

  let busy = false; // debounce rapid toggles
  buttons.forEach((btn) => {
    btn.addEventListener('click', async () => {
      if (busy) return;
      busy = true;
      const productId = Number(btn.dataset.productId);
      const handle = btn.dataset.productHandle;
      const wasSaved = btn.getAttribute('aria-pressed') === 'true';
      btn.setAttribute('aria-pressed', wasSaved ? 'false' : 'true'); // optimistic
      try {
        const state = wasSaved
          ? await FavoritesAPI.unsave(productId)
          : await FavoritesAPI.save(productId, handle);
        reflect(state);
      } catch (err) {
        btn.setAttribute('aria-pressed', wasSaved ? 'true' : 'false'); // revert
        if (err.signIn) {
          window.location.href = `/account/login?return_url=${encodeURIComponent(window.location.pathname)}`;
        } else {
          toast("Couldn't update favorites. Please try again.");
        }
      } finally {
        busy = false;
      }
    });
  });
}

/* ---------------- Favorites page ---------------- */

function formatSavedDate(iso) {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return `Saved ${d.toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' })}`;
}

async function fetchCard(saved) {
  const res = await fetch(`/products/${saved.handle}?section_id=favorites-card`);
  if (!res.ok) return null;
  const html = await res.text();
  const doc = new DOMParser().parseFromString(html, 'text/html');
  const card = doc.querySelector('[data-favorites-card]');
  if (!card) return null;
  const dateEl = card.querySelector('[data-favorites-saved-date]');
  if (dateEl) dateEl.textContent = formatSavedDate(saved.saved_at);
  return card;
}

function renderCollections(state, root) {
  const strip = root.querySelector('[data-favorites-collections]');
  const row = root.querySelector('[data-favorites-collections-row]');
  if (!strip || !row) return;
  strip.hidden = false;
  row.textContent = '';

  state.collections.forEach((col) => {
    // Built with DOM methods + textContent (NOT innerHTML): col.name is
    // user-supplied — string interpolation here would be an XSS vector.
    const el = document.createElement('article');
    el.className = 'coll';

    const cover = document.createElement('div');
    cover.className = 'coll__cover';

    const meta = document.createElement('div');
    meta.className = 'coll__meta';
    const name = document.createElement('div');
    name.className = 'coll__name';
    name.textContent = col.name;
    const count = document.createElement('div');
    count.className = 'coll__count';
    count.textContent = `${col.items.length} work${col.items.length === 1 ? '' : 's'}`;
    const actions = document.createElement('div');
    actions.className = 'coll__actions';
    const renameBtn = document.createElement('button');
    renameBtn.type = 'button';
    renameBtn.textContent = 'Rename';
    renameBtn.dataset.collectionRename = col.id;
    const deleteBtn = document.createElement('button');
    deleteBtn.type = 'button';
    deleteBtn.className = 'remove';
    deleteBtn.textContent = 'Delete';
    deleteBtn.dataset.collectionDelete = col.id;
    actions.append(renameBtn, deleteBtn);
    meta.append(name, count, actions);
    el.append(cover, meta);

    // Mosaic cover: clone images from already-rendered saved-work cards.
    const works = state.saved.filter((s) => col.items.includes(s.id)).slice(0, 4);
    works.forEach((w) => {
      const img = root.querySelector(`[data-favorites-card][data-product-id="${w.id}"] img`);
      if (img) cover.appendChild(img.cloneNode());
    });
    row.appendChild(el);
  });

  row.querySelectorAll('[data-collection-rename]').forEach((btn) =>
    btn.addEventListener('click', async () => {
      const newName = prompt('Collection name:');
      if (!newName) return;
      try {
        renderPage(await FavoritesAPI.collectionRename(btn.dataset.collectionRename, newName), root);
      } catch { toast("Couldn't rename collection."); }
    }));
  row.querySelectorAll('[data-collection-delete]').forEach((btn) =>
    btn.addEventListener('click', async () => {
      if (!confirm('Delete this collection? Saved works are kept.')) return;
      try {
        renderPage(await FavoritesAPI.collectionDelete(btn.dataset.collectionDelete), root);
      } catch { toast("Couldn't delete collection."); }
    }));
}

async function renderPage(state, root) {
  const grid = root.querySelector('[data-favorites-grid]');
  const empty = root.querySelector('[data-favorites-empty]');
  const countEl = root.querySelector('[data-favorites-count] .count');
  if (countEl) countEl.textContent = state.saved.length;

  grid.setAttribute('aria-busy', 'true');
  grid.textContent = '';
  empty.hidden = state.saved.length > 0;

  const cards = await Promise.all(state.saved.map((s) => fetchCard(s)));
  cards.filter(Boolean).forEach((card) => grid.appendChild(card));
  grid.setAttribute('aria-busy', 'false');

  // Card actions
  grid.querySelectorAll('[data-favorites-remove]').forEach((btn) =>
    btn.addEventListener('click', async () => {
      const card = btn.closest('[data-favorites-card]');
      card.style.transition = 'opacity 300ms ease';
      card.style.opacity = '0';
      try {
        const next = await FavoritesAPI.unsave(Number(btn.dataset.productId));
        setTimeout(() => renderPage(next, root), 300);
      } catch {
        card.style.opacity = '1';
        toast("Couldn't remove. Please try again.");
      }
    }));
  grid.querySelectorAll('[data-favorites-add-to-collection]').forEach((btn) =>
    btn.addEventListener('click', async () => {
      const state = FavoritesAPI.cached() || (await FavoritesAPI.get());
      const names = state.collections.map((c, i) => `${i + 1}. ${c.name}`).join('\n');
      const answer = prompt(`Add to which collection?\n${names}\n\nType a number, or a new name to create one:`);
      if (!answer) return;
      const index = parseInt(answer, 10) - 1;
      const productId = Number(btn.dataset.productId);
      try {
        let next;
        if (!Number.isNaN(index) && state.collections[index]) {
          next = await FavoritesAPI.collectionAdd(state.collections[index].id, productId);
        } else {
          next = await FavoritesAPI.collectionCreate(answer);
          const created = next.collections[next.collections.length - 1];
          next = await FavoritesAPI.collectionAdd(created.id, productId);
        }
        renderPage(next, root);
      } catch { toast("Couldn't add to collection."); }
    }));

  renderCollections(state, root);
}

function initFavoritesPage() {
  const root = document.querySelector('.main-favorites-section');
  if (!root || !root.querySelector('[data-favorites-grid]')) return;

  const errorEl = root.querySelector('[data-favorites-error]');
  const grid = root.querySelector('[data-favorites-grid]');
  const load = () =>
    FavoritesAPI.get()
      .then((state) => { errorEl.hidden = true; return renderPage(state, root); })
      .catch(() => {
        errorEl.hidden = false;
        grid.setAttribute('aria-busy', 'false');
      });

  root.querySelector('[data-favorites-retry]')?.addEventListener('click', load);
  root.querySelector('[data-favorites-create-collection]')?.addEventListener('click', async () => {
    const name = prompt('Collection name:');
    if (!name) return;
    try {
      renderPage(await FavoritesAPI.collectionCreate(name), root);
    } catch { toast("Couldn't create collection."); }
  });
  load();
}

/* ---------------- boot ---------------- */

window.ArterielFavorites = FavoritesAPI;

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => { initHearts(); initFavoritesPage(); });
} else {
  initHearts();
  initFavoritesPage();
}
